import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fileUrl, normalizeExternalUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { toast } from "sonner";
import CartWidget from "@/components/CartWidget";
import { useCart, canCart } from "@/lib/cart";
import { waLink, offerMessage, timeLeft, shareLink, imgFallback, offerImages } from "@/lib/offerUtils";
import { Search, Share2, Flame, Phone, Mail, Globe, MapPin, MessageCircle, Facebook, Instagram, Twitter, Linkedin, Tag, ExternalLink, Loader2, Store } from "lucide-react";

export default function PublicPage({ slugOverride }) {
  const params = useParams();
  const slug = slugOverride || params.slug;
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeCat, setActiveCat] = useState("all");
  const [query, setQuery] = useState("");
  const cart = useCart(slug);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/public/${slug}`);
        setData(res.data);
        api.post(`/public/${slug}/view`).catch(() => {});
      } catch {
        setNotFound(true);
      }
    })();
  }, [slug]);

  const clickOffer = async (offer) => {
    api.post(`/public/offer/${offer.offer_id}/click`).catch(() => {});
    if (offer.link_type === "external" && offer.external_url) {
      window.open(normalizeExternalUrl(offer.external_url), "_blank", "noopener");
    } else {
      navigate(slugOverride ? `/offer/${offer.offer_id}` : `/b/${slug}/offer/${offer.offer_id}`);
    }
  };

  if (notFound) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-5 text-center">
      <Store className="w-12 h-12 text-primary" />
      <h1 className="font-display text-2xl font-bold">Page not found</h1>
      <p className="text-muted-foreground">This business page doesn't exist or isn't published.</p>
      <Link to="/"><Button className="rounded-full bg-primary hover:bg-secondary">Go home</Button></Link>
    </div>
  );
  if (!data) return (<div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>);

  const { business: b, categories, offers } = data;
  const socials = [
    { url: b.facebook, icon: Facebook }, { url: b.instagram, icon: Instagram },
    { url: b.twitter, icon: Twitter }, { url: b.linkedin, icon: Linkedin },
  ].filter((s) => s.url);
  const q = query.trim().toLowerCase();
  const shown = offers
    .filter((o) => activeCat === "all" || o.category_id === activeCat)
    .filter((o) => !q || `${o.title} ${o.description} ${o.price}`.toLowerCase().includes(q));
  const offerUrl = (o) => window.location.origin + (slugOverride ? `/offer/${o.offer_id}` : `/b/${slug}/offer/${o.offer_id}`);
  const pageUrl = window.location.origin + (slugOverride ? "/" : `/b/${slug}`);
  const onShare = async () => { const r = await shareLink(b.name, pageUrl); api.post(`/public/${slug}/event`, { type: "share" }).catch(() => {}); if (r === "copied") toast.success("Link copied"); };

  return (
    <div className="min-h-screen" data-public-theme={b.theme}>
      {/* Hero */}
      <div className="relative h-[42vh] min-h-[300px] w-full overflow-hidden">
        {b.cover_path ? <img src={fileUrl(b.cover_path)} alt={b.name} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-secondary/30" />}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-black/40" />
        <div className="absolute inset-x-0 bottom-0">
          <div className="max-w-5xl mx-auto px-5 pb-8 flex items-end gap-4">
            {b.logo_path ? <img src={fileUrl(b.logo_path)} alt="logo" className="w-20 h-20 rounded-xl object-cover border border-white/20" /> : <div className="w-20 h-20 rounded-xl bg-secondary flex items-center justify-center"><Store className="w-8 h-8 text-primary" /></div>}
            <div>
              {b.business_type && <span className="overline text-primary">{b.business_type}</span>}
              <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="public-business-name">{b.name}</h1>
              {b.tagline && <p className="text-muted-foreground mt-1">{b.tagline}</p>}
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-5 py-8">
        {b.description && <p className="text-muted-foreground max-w-2xl">{b.description}</p>}
        {b.mode === "food" && (
          <div className={`mt-4 rounded-xl border px-4 py-3 text-sm ${b.accepting_orders ? "border-primary/40 bg-primary/10" : "border-white/10 bg-card text-muted-foreground"}`} data-testid="food-banner">
            {b.accepting_orders ? "🍽️ Pre-order now — skip the queue. Pick your food, get a token number, collect when it's ready." : "⏸️ This shop isn't taking orders right now. Check back soon."}
          </div>
        )}

        <div className="mt-6 flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search offers..." className="w-full pl-9 pr-3 py-2 rounded-full bg-card border border-white/10 text-sm outline-none focus:border-primary" data-testid="public-search" />
          </div>
          <Button variant="outline" onClick={onShare} className="rounded-full border-white/15" data-testid="share-page-btn"><Share2 className="w-4 h-4 mr-1" /> Share</Button>
        </div>

        {/* Category filter */}
        {categories.length > 0 && (
          <div className="mt-8 flex flex-wrap gap-2">
            <button onClick={() => setActiveCat("all")} className={`px-4 py-1.5 rounded-full text-sm transition-colors ${activeCat === "all" ? "bg-primary text-white" : "bg-card border border-white/10 text-muted-foreground hover:text-white"}`} data-testid="cat-filter-all">All</button>
            {categories.map((c) => (
              <button key={c.category_id} onClick={() => setActiveCat(c.category_id)} className={`px-4 py-1.5 rounded-full text-sm transition-colors ${activeCat === c.category_id ? "bg-primary text-white" : "bg-card border border-white/10 text-muted-foreground hover:text-white"}`} data-testid={`cat-filter-${c.category_id}`}>{c.name}</button>
            ))}
          </div>
        )}

        {/* Offers */}
        <h2 className="font-display text-xl font-bold tracking-tight mt-8">Offers & deals</h2>
        {shown.length === 0 ? (
          <p className="text-muted-foreground mt-4" data-testid="public-no-offers">No offers in this category yet.</p>
        ) : (
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {shown.map((o, i) => (
              <motion.div
                role="button" tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); clickOffer(o); } }}
                key={o.offer_id}
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: i * 0.05 }}
                onClick={() => clickOffer(o)}
                className="text-left cursor-pointer bg-card border border-white/10 rounded-xl overflow-hidden hover:border-primary/50 transition-colors group flex flex-col"
                data-testid={`public-offer-${o.offer_id}`}
              >
                <div className="aspect-video bg-secondary/20 relative overflow-hidden">
                  <div className="absolute inset-0 flex items-center justify-center"><Tag className="w-8 h-8 text-primary/40" /></div>
                  {offerImages(o)[0] && <img src={fileUrl(offerImages(o)[0])} alt={o.title} loading="lazy" onError={imgFallback} className={`relative w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${o.sold_out ? "grayscale opacity-60" : ""}`} />}
                  {o.sold_out && <span className="absolute inset-x-0 bottom-2 mx-auto w-fit bg-black/80 text-xs px-3 py-1 rounded-full">Sold out</span>}
                  {o.featured && <span className="absolute bottom-2 left-2 bg-yellow-500 text-black text-xs font-semibold px-2 py-0.5 rounded-full">★ Featured</span>}
                  {timeLeft(o.expires_at) && <span className="absolute bottom-2 right-2 bg-black/70 text-xs px-2 py-0.5 rounded-full" data-testid="offer-countdown">⏱ {timeLeft(o.expires_at)}</span>}
                  {o.badge && <Badge className="absolute top-2 left-2 bg-primary">{o.badge}</Badge>}
                  {o.link_type === "external" && <span className="absolute top-2 right-2 bg-black/60 rounded-full p-1.5"><ExternalLink className="w-3 h-3" /></span>}
                </div>
                <div className="p-4">
                  <h3 className="font-display font-semibold">{o.title}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    {o.discount && <span className="text-primary font-bold">{o.discount}</span>}
                    {o.price && <span className="text-sm text-muted-foreground">{o.price}</span>}
                  </div>
                  {o.description && <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{o.description}</p>}
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-xs text-primary">{o.link_type === "external" ? "Visit →" : "View details →"}</span>
                    {canCart(b) && !o.sold_out && (
                      <span role="button" tabIndex={0} data-testid={`add-cart-${o.offer_id}`}
                        onClick={(e) => { e.stopPropagation(); cart.add(o); toast.success("Added to cart"); }}
                        className="text-xs px-3 py-1 rounded-full border border-primary/50 text-primary hover:bg-primary hover:text-white transition-colors">+ Add to cart</span>
                    )}
                  </div>
                  {b.whatsapp && (
                    <a href={waLink(b.whatsapp, offerMessage(b.name, o, offerUrl(o)))} target="_blank" rel="noopener noreferrer"
                      onClick={(e) => { e.stopPropagation(); api.post(`/public/${slug}/event`, { type: "whatsapp", offer_id: o.offer_id }).catch(() => {}); }}
                      className="mt-3 flex items-center justify-center gap-2 rounded-full bg-green-600 hover:bg-green-700 text-white text-sm font-semibold py-2 transition-colors"
                      data-testid={`wa-offer-${o.offer_id}`}>
                      <MessageCircle className="w-4 h-4" /> {o.sold_out ? "Ask on WhatsApp" : b.mode === "food" ? "Ask / order on WhatsApp" : "Chat on WhatsApp"}
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Contact & map */}
        <div className="grid lg:grid-cols-2 gap-6 mt-12">
          <div className="bg-card border border-white/10 rounded-xl p-6">
            <h3 className="font-display font-semibold">Get in touch</h3>
            <div className="mt-4 space-y-3 text-sm">
              {b.phone && <a href={`tel:${b.phone}`} className="flex items-center gap-3 hover:text-primary" data-testid="contact-phone"><Phone className="w-4 h-4 text-primary" />{b.phone}</a>}
              {b.whatsapp && <a href={`https://wa.me/${b.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 hover:text-primary" data-testid="contact-whatsapp"><MessageCircle className="w-4 h-4 text-primary" />WhatsApp</a>}
              {b.email && <a href={`mailto:${b.email}`} className="flex items-center gap-3 hover:text-primary" data-testid="contact-email"><Mail className="w-4 h-4 text-primary" />{b.email}</a>}
              {b.website && <a href={b.website} target="_blank" rel="noreferrer" className="flex items-center gap-3 hover:text-primary" data-testid="contact-website"><Globe className="w-4 h-4 text-primary" />{b.website}</a>}
              {b.address && <p className="flex items-center gap-3"><MapPin className="w-4 h-4 text-primary" />{b.address}</p>}
            </div>
            {socials.length > 0 && (
              <div className="flex gap-3 mt-5">
                {socials.map((s, i) => (
                  <a key={i} href={s.url} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-secondary/40 flex items-center justify-center hover:bg-primary transition-colors" data-testid={`social-${i}`}>
                    <s.icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            )}
          </div>
          {(b.map_query || b.address) && (
            <div className="bg-card border border-white/10 rounded-xl overflow-hidden min-h-[220px]">
              <iframe
                title="map"
                className="w-full h-full min-h-[220px]"
                src={`https://maps.google.com/maps?q=${encodeURIComponent(b.map_query || b.address)}&output=embed`}
                data-testid="business-map"
              />
            </div>
          )}
        </div>
      </main>

      <CartWidget slug={slug} business={b} pageUrl={pageUrl} trackPath={(id) => (slugOverride ? `/order/${id}` : `/b/${slug}/order/${id}`)} />
      {b.whatsapp && (
        <a href={waLink(b.whatsapp, `Hi ${b.name}, I saw your offers on DealWisp.`)} target="_blank" rel="noreferrer" className="fixed bottom-4 right-4 z-40 bg-green-500 hover:bg-green-600 text-white rounded-full shadow-lg px-5 py-3 flex items-center gap-2 text-sm font-semibold" data-testid="sticky-whatsapp" onClick={() => api.post(`/public/${slug}/event`, { type: "whatsapp" }).catch(() => {})}>
          <MessageCircle className="w-5 h-5" /> Chat on WhatsApp
        </a>
      )}

      <footer className="border-t border-white/10 py-6 mt-8 pb-20">
        <div className="max-w-5xl mx-auto px-5 flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Flame className="w-4 h-4 text-primary" /> Powered by <Link to="/" className="text-primary hover:underline">DealWisp</Link>
          <span>·</span>
          <button className="hover:text-white underline" data-testid="report-page-btn" onClick={async () => {
            const reason = window.prompt("What's wrong with this page? (spam, fake offers, abuse...)");
            if (!reason || reason.trim().length < 3) return;
            try { await api.post(`/public/${slug}/report`, { reason }); toast.success("Thanks — we'll review this page."); } catch { toast.error("Couldn't send the report"); }
          }}>Report</button>
        </div>
      </footer>
    </div>
  );
}
